import React, { useState, useEffect } from 'react';
import { SiteReadingCard } from '../../../../components/SiteReadingCard';
import { SiteDevicesCard } from '../../../../components/SiteDevicesCard';
import { useLanguageLocale } from '../../../../hooks/language-locale-hooks';
import { GenericToolbar } from '../../../../components/GenericToolbar';
import { pushAppRoute } from '../../../../data/domain/route-manager';
import { useSelectedSiteData, useSelectedLocationData, isRecordFound } from '../../../../hooks/data-hooks';
import { useHistory } from 'react-router-dom';
import { ContentContainer } from '../../../../components/ContentContainer';
import { RecordDataIndicators } from '../../../../components/RecordDataIndicators';
import { useSiteDevicesData } from '../../../../data/DataProviders/SiteDevicesDataProvider';
import { useSiteOnlineStatusData } from '../../../../data/DataProviders/SiteOnlineStatusDataProvider';
import { useSiteChannelData } from '../../../../data/DataProviders/SiteChannelDataProvider';
import { SiteTimelineCard } from '../../../../components/SiteTimelineCard';
import { useTimelineData } from '../../../../data/DataProviders/TimelineDataProvider';
import { TrendDataProvider } from '../../../../data/DataProviders/TrendDataProvider';
import { TrendsCard } from '../../../../components/Trends';
import { useAppRoute } from '../../../../hooks/app-route-hooks';
import { ExportPopUp } from '../../../../components/ExportPopUp/ExportPopUp';
import { useSiteAlarmCount } from '../../../../data/DataProviders/SiteAlarmCountProvider';
import { useSiteBatteryStatus } from '../../../../data/DataProviders/SiteBatteryStatusDataProvider';
import { ToolbarColor } from '../../../../components/GenericToolbar/GenericToolbar';
import { isDataLoading, isDataError, DataState, isDataLoaded, Data, ChannelDatum } from '../../../../types';
import { SignalRSite } from '../../../../types/signalr-types';
import { getUpdateChannelDataWithDevideId, getUpdatedDeviceStatusList } from '../../../../lib/signalr-helper';
import { useSignalRSiteData } from '../../../../data/DataProviders/SignalRSiteDataProvider';
import makeStyles from '@mui/styles/makeStyles';
import { Theme, List, Card, Typography, Link } from '@mui/material';

import { ReusableTrendsCardHeader } from '../../../../components/ReusableTrendsGraph';
import { TrendsCardHeaderNotLoaded } from '../../../../components/ReusableTrendsGraph/ReusableTrendCardContentLoader';
import { useGetOrganizationCheckDetails } from '../../../../data/DataProviders/GetOrgCheckProvider';
import { useGetSiteSubscriptionDetails } from '../../../../data/DataProviders/GetSiteSubscriptionProvider';
import { ShowChart } from '@mui/icons-material';
import { hideTrends } from '../../../../lib/hide-trends-helper';
import { EmptyState } from '@brightlayer-ui/react-components';
import { CONTACT_EMAIL } from '../../../../constants';

type deviceStatusListType = {
    id: string;
    name: string;
    type: string | null;
    iconClass: string;
    status: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
    devicesCard: {
        width: '360px',
    },
    timelineCard: {
        width: '920px',
        [theme.breakpoints.between(1105, 'xl')]: {
            // eslint-disable-line no-useless-computed-key
            width: '700px',
        },
        [theme.breakpoints.between(760, 1104)]: {
            // eslint-disable-line no-useless-computed-key
            width: '360px',
        },
    },
}));

const getSiteAlarmStatus = (siteAlarmCount: ReturnType<typeof useSiteAlarmCount>): { hasAlarm: boolean } => {
    if (isDataLoading(siteAlarmCount) || isDataError(siteAlarmCount)) {
        return { hasAlarm: false };
    }
    return { hasAlarm: siteAlarmCount && siteAlarmCount.value && siteAlarmCount.value > 0 ? true : false };
};

export const OverviewSiteSummary: React.FC = () => {
    const selectedLocationData = useSelectedLocationData();
    const selectedSiteData = useSelectedSiteData();
    const currentAppRoute = useAppRoute('OverviewSite');
    const history = useHistory();
    const { t } = useLanguageLocale();
    const siteDevicesData = useSiteDevicesData();
    const siteOnlineData = useSiteOnlineStatusData();
    const siteAlarmCount = useSiteAlarmCount();
    const siteChannelData = useSiteChannelData();
    const timelineData = useTimelineData();
    const siteBatteryStatus = useSiteBatteryStatus();
    const siteRealTimeData = useSignalRSiteData();
    const found = isRecordFound(selectedLocationData) && isRecordFound(selectedSiteData);
    const [open, setOpen] = useState(false);
    const { hasAlarm } = getSiteAlarmStatus(siteAlarmCount);
    const openExportPopup = (value: boolean): void => setOpen(value);
    const classes = useStyles();
    const getOrgCheckDetails = useGetOrganizationCheckDetails();
    const [getOrgTypeData, setgetOrgTypeData] = useState<any | null>(null);

    const meshSiteSubscriptionDetails = useGetSiteSubscriptionDetails();

    const [trendsEnabled, setTrendsEnabled] = React.useState<boolean>(false);
    const [showError, setShowError] = useState(false);

    const [alarm, setAlarm] = useState(false);
    const [channelData, setChannelData] = useState<Data<ChannelDatum[]>>({ state: DataState.Loading });
    const [batteryStatus, setBatteryStatus] = useState<Data<boolean | null>>({ state: DataState.Loading });
    const [siteOnline, setSiteOnline] = useState<Data<object | undefined>>({ state: DataState.Loading });
    const [deviceStatusList, setDeviceStatusList] = useState<Data<deviceStatusListType[]>>({
        state: DataState.Loading,
    });
    const [showTrends, setShowTrends] = useState(false);

    const getUpdatedChannelData = (updatedSite: SignalRSite): void => {
        if (updatedSite.channelData && updatedSite.channelData.length > 0) {
            const clone = getUpdateChannelDataWithDevideId(updatedSite.channelData, siteChannelData);

            setChannelData(clone);
        }
    };

    const getUpdatedDeviceStatus = (updatedSite: SignalRSite): void => {
        if (updatedSite.devices && updatedSite.devices.length > 0) {
            const clone = getUpdatedDeviceStatusList(updatedSite.devices, siteDevicesData);
            setDeviceStatusList(clone);
        }
    };

    const getUpdatedConnectionStatus = (updatedSite: SignalRSite): void => {
        if (isDataLoaded(siteOnlineData) && updatedSite.connectionStatus !== undefined) {
            if (updatedSite.connectionStatus !== null) {
                siteOnlineData.value.status = updatedSite.connectionStatus;
                const clone = Object.assign({}, siteOnlineData); //due to shallow comparison , clone to new object
                setSiteOnline(clone);
            }
        }
    };
    const getUpdatedBatteryStatus = (updatedSite: SignalRSite): void => {
        if (isDataLoaded(siteBatteryStatus) && updatedSite.batteryStatus) {
            siteBatteryStatus.value =
                updatedSite.batteryStatus.toLowerCase() === 'true' || updatedSite.batteryStatus === '' ? true : false;
            const clone = Object.assign({}, siteBatteryStatus); //due to shallow comparison , clone to new object
            setBatteryStatus(clone);
        }
    };
    useEffect(() => {
        if (isDataLoaded(getOrgCheckDetails)) {
            setgetOrgTypeData(getOrgCheckDetails.value);
        }
    }, [getOrgCheckDetails]);

    useEffect(() => {
        if (meshSiteSubscriptionDetails !== null) {
            if (isDataLoaded(meshSiteSubscriptionDetails)) {
                setTrendsEnabled(meshSiteSubscriptionDetails.value.cloudDataSync.isTrendsEnabled);
                setShowTrends(true);

                const error = hideTrends(meshSiteSubscriptionDetails.value);
                setShowError(error);
            }
            if (isDataError(meshSiteSubscriptionDetails)) {
                setShowTrends(false);
            }
        }
    }, [meshSiteSubscriptionDetails]);

    useEffect(() => {
        setAlarm(hasAlarm);
        setChannelData(siteChannelData);
        setBatteryStatus(siteBatteryStatus);
        setSiteOnline(siteOnlineData);
        setDeviceStatusList(siteDevicesData);
    }, [hasAlarm, siteChannelData, siteBatteryStatus, siteDevicesData, siteOnlineData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isRecordFound(selectedLocationData) && isRecordFound(selectedSiteData) && siteRealTimeData) {
            if (siteRealTimeData.alarmStatus && siteRealTimeData.alarmStatus.length > 0)
                setAlarm(siteRealTimeData.alarmCount > 0);
            getUpdatedChannelData(siteRealTimeData);
            getUpdatedConnectionStatus(siteRealTimeData);
            getUpdatedBatteryStatus(siteRealTimeData);
            getUpdatedDeviceStatus(siteRealTimeData);
        }
    }, [siteRealTimeData]); // eslint-disable-line react-hooks/exhaustive-deps

    const toolbarColor = (): ToolbarColor => {
        if (alarm) return ToolbarColor.Error;
        return ToolbarColor.Default;
    };

    return (
        <>
            <GenericToolbar
                page="OverviewSiteSummary"
                showBackButton
                backButtonOnClick={(): void => {
                    pushAppRoute(history, {
                        type: 'OverviewLocation',
                        locationId: currentAppRoute.locationId,
                    });
                }}
                color={toolbarColor()}
                title={found && isRecordFound(selectedSiteData) ? selectedSiteData.value.name : ''}
                subtitle={found && isRecordFound(selectedLocationData) ? selectedLocationData.value.name : ''}
            />

            <ContentContainer justifyContent="center" testId="site-details-page" marginleft="0px" marginRight="0px">
                <RecordDataIndicators
                    recordData={selectedSiteData}
                    errorMessage={t('DASHBOARD.ERROR_LOADING_SITE')}
                    notFoundIcon="Business"
                    notFoundMessage={t('DASHBOARD.SITE_NOT_FOUND')}
                />
                {isRecordFound(selectedSiteData) && isRecordFound(selectedLocationData) && (
                    <>
                        <div style={{ margin: '5px 15px 0px 0px' }} className={classes.devicesCard}>
                            <SiteReadingCard
                                channelData={channelData}
                                onlineStatusData={siteOnline}
                                batteryStatusData={batteryStatus}
                                readingsAppRoute={{
                                    type: 'OverviewSiteReadings',
                                    siteId: currentAppRoute.siteId,
                                    locationId: currentAppRoute.locationId,
                                }}
                                testId="site-readings-card"
                            />

                            <SiteDevicesCard
                                devicesData={deviceStatusList}
                                maxItemsToDisplay={5}
                                deviceDetailAppRoute={{
                                    type: 'OverviewSiteDeviceDetails',
                                    siteId: currentAppRoute.siteId,
                                    locationId: currentAppRoute.locationId,
                                    deviceId: '--',
                                }}
                                viewAllDevicesAppRoute={{
                                    type: 'OverviewSiteDevices',
                                    siteId: currentAppRoute.siteId,
                                    locationId: currentAppRoute.locationId,
                                }}
                                style={{ marginTop: '20px' }}
                            />
                        </div>

                        <div style={{ margin: '5px' }} className={classes.timelineCard}>
                            <SiteTimelineCard
                                testId="site-timeline-card"
                                timelineData={timelineData}
                                selectedSite={selectedSiteData.value.id}
                                selectedLocation={selectedLocationData.value.id}
                                showExportPopup={openExportPopup}
                                style={{
                                    marginBottom: 15,
                                }}
                                viewAllEventsAppRoute={{
                                    type: 'OverviewSiteTimeline',
                                    siteId: currentAppRoute.siteId,
                                    locationId: currentAppRoute.locationId,
                                }}
                                eventDetailAppRoute={{
                                    type: 'OverviewSiteEventDetails',
                                    siteId: currentAppRoute.siteId,
                                    locationId: currentAppRoute.locationId,
                                    alarmId: '--',
                                    deviceId: '--',
                                }}
                            />
                            <>
                                {/* {meshSiteSubscriptionDetails !== null &&
                                    isDataLoaded(meshSiteSubscriptionDetails) && */}

                                {(getOrgTypeData && trendsEnabled && !showError && showTrends) ||
                                getOrgTypeData === null ? (
                                    <TrendDataProvider siteId={selectedSiteData.value.id}>
                                        <TrendsCard
                                            viewTrendviewerAppRoute={{
                                                type: 'OverviewSiteTrends',
                                                siteId: currentAppRoute.siteId,
                                                locationId: currentAppRoute.locationId,
                                            }}
                                        />
                                    </TrendDataProvider>
                                ) : (
                                    ((meshSiteSubscriptionDetails !== null &&
                                        isDataLoaded(meshSiteSubscriptionDetails) &&
                                        showError === false) ||
                                        (meshSiteSubscriptionDetails !== null &&
                                            isDataError(meshSiteSubscriptionDetails)) ||
                                        trendsEnabled === false) && (
                                        <>
                                            <Card
                                                style={{
                                                    margin: '15px',
                                                    marginTop: 5,
                                                    marginBottom: 25,
                                                    flex: 1,
                                                }}
                                            >
                                                <List
                                                    disablePadding
                                                    subheader={
                                                        <ReusableTrendsCardHeader
                                                            title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}
                                                        >
                                                            <TrendsCardHeaderNotLoaded />
                                                        </ReusableTrendsCardHeader>
                                                    }
                                                >
                                                    <EmptyState
                                                        icon={<ShowChart id="fail-icon" style={{ fontSize: '24px' }} />}
                                                        title={'No Trends'}
                                                        actions={
                                                            <>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    // color="primary"
                                                                    style={{ textAlign: 'center' }}
                                                                >
                                                                    {'You don’t have access to trends.'}
                                                                </Typography>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    // color="primary"
                                                                    style={{ textAlign: 'center' }}
                                                                >
                                                                    {'Contact Eaton support at'}{' '}
                                                                    <Link
                                                                        underline="always"
                                                                        href={`mailto:${CONTACT_EMAIL}`}
                                                                    >
                                                                        {CONTACT_EMAIL}
                                                                    </Link>{' '}
                                                                </Typography>
                                                            </>
                                                        }
                                                    />
                                                </List>
                                            </Card>
                                        </>
                                    )
                                )}
                            </>
                        </div>
                        {open && (
                            <ExportPopUp
                                for="Site"
                                open={open}
                                onClose={(): void => openExportPopup(false)}
                                siteDetails={selectedSiteData.value}
                            ></ExportPopUp>
                        )}
                    </>
                )}
            </ContentContainer>
        </>
    );
};
